import React, { createContext, useContext, useState, useEffect } from 'react';
import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    onSnapshot,
    query,
    orderBy,
    serverTimestamp,
    where
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../lib/firebase';
import { useAuth } from './AuthContext';
import { useLocalStorage } from '../hooks/useLocalStorage';
import {
    mockAnalytics,
    mockInsights,
    mockAutomationSettings,
    mockAIDecisions,
    mockBrands,
    mockCreatives
} from '../utils/mockData';

const AppContext = createContext();

export const useApp = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useApp must be used within AppProvider');
    }
    return context;
};

export const AppProvider = ({ children }) => {
    const { currentUser } = useAuth();
    const [theme, setTheme] = useLocalStorage('theme', 'dark');
    const [lastSelectedBrandId, setLastSelectedBrandId] = useLocalStorage('lastSelectedBrandId', null);

    // Real-time data from Firestore
    const [brands, setBrands] = useState([]);
    const [creatives, setCreatives] = useState([]);
    const [scheduledPosts, setScheduledPosts] = useState([]);
    const [team, setTeam] = useState([]);
    const [activityLog, setActivityLog] = useState([]);
    const [selectedBrand, setSelectedBrand] = useState(null);

    // Mock data for complex features (Analytics/AI)
    const [automationSettings, setAutomationSettings] = useState(mockAutomationSettings);
    const [aiDecisions] = useState(mockAIDecisions);
    const [insights] = useState(mockInsights);
    const [analytics] = useState(mockAnalytics);

    // Billing and credits
    const [creditBalance, setCreditBalance] = useState(0);
    const [subscriptionStatus, setSubscriptionStatus] = useState('inactive');
    const [subscriptionTier, setSubscriptionTier] = useState(null);
    const [billingLoading, setBillingLoading] = useState(true);

    // Toggle theme
    const toggleTheme = () => {
        const newTheme = theme === 'dark' ? 'light' : 'dark';
        setTheme(newTheme);
        document.documentElement.setAttribute('data-theme', newTheme);
    };

    // Fetch and subscribe to user billing
    useEffect(() => {
        if (!currentUser) {
            setBillingLoading(false);
            return;
        }

        const fetchBilling = async () => {
            try {
                const response = await fetch(
                    `http://localhost:3001/api/billing/balance?userId=${currentUser.uid}`
                );
                const data = await response.json();

                setCreditBalance(data.creditBalance || 0);
                setSubscriptionStatus(data.subscriptionStatus || 'inactive');
                setSubscriptionTier(data.subscriptionTier);
                setBillingLoading(false);
            } catch (error) {
                console.error('Error fetching billing:', error);
                setBillingLoading(false);
            }
        };

        fetchBilling();

        // Real-time subscription to billing updates
        const billingRef = doc(db, 'users', currentUser.uid, 'billing', 'current');
        const unsubBilling = onSnapshot(billingRef,
            (doc) => {
                if (doc.exists()) {
                    const billing = doc.data();
                    setCreditBalance(billing.creditBalance || 0);
                    setSubscriptionStatus(billing.subscriptionStatus || 'inactive');
                    setSubscriptionTier(billing.subscriptionTier);
                }
            },
            (error) => {
                console.error('Error subscribing to billing:', error);
            }
        );

        return () => unsubBilling();
    }, [currentUser]);

    // Subscribe to Firestore collections
    useEffect(() => {
        if (!currentUser) {
            console.log('AppContext: No currentUser, skipping Firestore subscriptions');
            return;
        }

        console.log('AppContext: Setting up Firestore subscriptions for user:', currentUser.email);

        // Brands
        const qBrands = query(collection(db, 'brands'), orderBy('createdAt', 'desc'));
        const unsubBrands = onSnapshot(qBrands,
            async (snapshot) => {
                const brandsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                console.log('AppContext: Brands loaded from Firestore:', brandsData.length, 'brands');
                setBrands(brandsData);

                // Auto-fix brands missing userId (for platform connections)
                for (const brand of brandsData) {
                    if (!brand.userId && brand.createdBy) {
                        // Use createdBy as userId if missing
                        try {
                            const brandRef = doc(db, 'brands', brand.id);
                            await updateDoc(brandRef, { userId: brand.createdBy });
                            console.log('AppContext: Fixed missing userId for brand:', brand.name);
                        } catch (err) {
                            console.error('AppContext: Failed to fix userId for brand:', brand.name, err);
                        }
                    } else if (!brand.userId && currentUser) {
                        // If no createdBy either, use current user
                        try {
                            const brandRef = doc(db, 'brands', brand.id);
                            await updateDoc(brandRef, { userId: currentUser.uid });
                            console.log('AppContext: Set userId for brand:', brand.name);
                        } catch (err) {
                            console.error('AppContext: Failed to set userId for brand:', brand.name, err);
                        }
                    }
                }

                // Restore previously selected brand or select the first one
                if (brandsData.length > 0 && !selectedBrand) {
                    const lastBrand = brandsData.find(b => b.id === lastSelectedBrandId);
                    setSelectedBrand(lastBrand || brandsData[0]);
                }
            },
            (error) => {
                console.error('AppContext: Error loading brands from Firestore:', error);
            }
        );

        // Creatives
        const qCreatives = query(collection(db, 'creatives'), orderBy('createdAt', 'desc'));
        const unsubCreatives = onSnapshot(qCreatives, (snapshot) => {
            setCreatives(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        // Scheduled Posts
        const qPosts = query(collection(db, 'posts'), orderBy('scheduledDate', 'asc'));
        const unsubPosts = onSnapshot(qPosts, (snapshot) => {
            setScheduledPosts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        // Team
        const qTeam = query(collection(db, 'team'), orderBy('joinedAt', 'desc'));
        const unsubTeam = onSnapshot(qTeam, (snapshot) => {
            setTeam(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        // Activity Log
        const qActivity = query(collection(db, 'activity'), orderBy('timestamp', 'desc'));
        const unsubActivity = onSnapshot(qActivity, (snapshot) => {
            setActivityLog(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        return () => {
            unsubBrands();
            unsubCreatives();
            unsubPosts();
            unsubTeam();
            unsubActivity();
        };
    }, [currentUser]);

    // Helper to log activity
    const logActivity = async (action, target) => {
        if (!currentUser) return;
        try {
            await addDoc(collection(db, 'activity'), {
                user: currentUser.displayName || currentUser.email,
                action,
                target,
                timestamp: new Date().toISOString()
            });
        } catch (error) {
            console.error("Error logging activity:", error);
        }
    };

    // Document Upload
    const uploadDocument = async (file, path) => {
        if (!currentUser) throw new Error("User not authenticated");

        const storageRef = ref(storage, path);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);

        return {
            name: file.name,
            url: downloadURL,
            type: file.type,
            size: file.size,
            uploadedAt: new Date().toISOString()
        };
    };

    // Brand operations
    const addBrand = async (brand) => {
        try {
            await addDoc(collection(db, 'brands'), {
                ...brand,
                createdAt: new Date().toISOString(),
                createdBy: currentUser.uid,
                userId: currentUser.uid  // Used by server for platform connections lookup
            });
            logActivity('created brand', brand.name);
        } catch (error) {
            console.error("Error adding brand:", error);
            throw error;
        }
    };

    const updateBrand = async (id, updates) => {
        try {
            const brandRef = doc(db, 'brands', id);
            // Always ensure userId is set for platform connections lookup
            const updatesWithUser = {
                ...updates,
                userId: currentUser.uid
            };
            await updateDoc(brandRef, updatesWithUser);
            logActivity('updated brand', 'Brand Profile');
        } catch (error) {
            console.error("Error updating brand:", error);
            throw error;
        }
    };

    const deleteBrand = async (id) => {
        try {
            await deleteDoc(doc(db, 'brands', id));
            logActivity('deleted brand', 'Brand Profile');
        } catch (error) {
            console.error("Error deleting brand:", error);
            throw error;
        }
    };

    // Creative operations
    const addCreative = async (creative) => {
        try {
            const docRef = await addDoc(collection(db, 'creatives'), {
                ...creative,
                createdAt: new Date().toISOString(),
                createdBy: currentUser.displayName || currentUser.email,
                userId: currentUser.uid,
                status: 'draft',
            });
            logActivity('generated creative', creative.title);
            return { id: docRef.id, ...creative };
        } catch (error) {
            console.error("Error adding creative:", error);
            throw error;
        }
    };

    const updateCreative = async (id, updates) => {
        try {
            const creativeRef = doc(db, 'creatives', id);
            await updateDoc(creativeRef, updates);
        } catch (error) {
            console.error("Error updating creative:", error);
            throw error;
        }
    };

    const deleteCreative = async (id) => {
        try {
            await deleteDoc(doc(db, 'creatives', id));
            logActivity('deleted creative', 'Asset');
        } catch (error) {
            console.error("Error deleting creative:", error);
            throw error;
        }
    };

    // Schedule post
    // creativeData is optional - if provided, use it instead of looking up in creatives array
    // This supports scheduling library items (carousels, etc.) that aren't in the creatives collection
    const schedulePost = async (creativeId, platforms, date, timezone = 'EST', creativeData = null) => {
        try {
            // Use provided creative data or look up in creatives array
            const creative = creativeData || creatives.find(c => c.id === creativeId);
            if (!creative) {
                console.error('schedulePost: Creative not found for ID:', creativeId);
                throw new Error('Creative not found');
            }

            await addDoc(collection(db, 'posts'), {
                creativeId,
                creativeTitle: creative.title,
                brandName: creative.brandName,
                brandId: creative.brandId,
                platforms,
                scheduledDate: date,
                timezone,
                status: 'scheduled',
                createdAt: new Date().toISOString(),
                // Store additional metadata for library items
                creativeType: creative.type || 'image',
                imageUrl: creative.imageUrl || creative.url,
                source: creative.source || 'context',
                // Post content
                caption: creative.caption || '',
                hashtags: creative.hashtags || '',
                // Store carousel images for publishing
                carouselImages: creative.carouselImages || null
            });

            // Only update creative status if it's from the creatives collection
            if (!creativeData || creative.source === 'context') {
                await updateCreative(creativeId, { status: 'scheduled', scheduledDate: date });
            }
            logActivity('scheduled post', creative.title);
        } catch (error) {
            console.error("Error scheduling post:", error);
            throw error;
        }
    };

    // Unschedule post - remove from schedule and set creative back to draft
    const unschedulePost = async (creativeId) => {
        try {
            // Find and delete the scheduled post
            const postToDelete = scheduledPosts.find(p => p.creativeId === creativeId);
            if (postToDelete) {
                await deleteDoc(doc(db, 'posts', postToDelete.id));
            }

            // Update creative status back to draft
            await updateCreative(creativeId, {
                status: 'draft',
                scheduledDate: null
            });

            const creative = creatives.find(c => c.id === creativeId);
            logActivity('unscheduled post', creative?.title || 'Creative');
        } catch (error) {
            console.error("Error unscheduling post:", error);
            throw error;
        }
    };

    // Reschedule post - update the scheduled date
    const reschedulePost = async (creativeId, newDate, newTimezone = null) => {
        try {
            // Find the scheduled post
            const postToUpdate = scheduledPosts.find(p => p.creativeId === creativeId);
            if (postToUpdate) {
                const updates = { scheduledDate: newDate };
                if (newTimezone) {
                    updates.timezone = newTimezone;
                }
                await updateDoc(doc(db, 'posts', postToUpdate.id), updates);
            }

            // Update creative's scheduled date
            await updateCreative(creativeId, { scheduledDate: newDate });

            const creative = creatives.find(c => c.id === creativeId);
            logActivity('rescheduled post', creative?.title || 'Creative');
        } catch (error) {
            console.error("Error rescheduling post:", error);
            throw error;
        }
    };

    // Team operations
    const addTeamMember = async (member) => {
        try {
            await addDoc(collection(db, 'team'), {
                ...member,
                joinedAt: new Date().toISOString(),
                invitedBy: currentUser.uid
            });
            logActivity('invited member', member.email);
        } catch (error) {
            console.error("Error adding team member:", error);
            throw error;
        }
    };

    const updateTeamMember = async (id, updates) => {
        try {
            const memberRef = doc(db, 'team', id);
            await updateDoc(memberRef, updates);
        } catch (error) {
            console.error("Error updating team member:", error);
            throw error;
        }
    };

    // Helper functions for credits
    const canAfford = (estimatedCredits) => {
        return creditBalance >= estimatedCredits;
    };

    const formatCredits = (credits) => {
        return credits.toLocaleString();
    };

    const creditsToUSD = (credits) => {
        return (credits / 100000).toFixed(2);
    };

    const hasActiveSubscription = () => {
        return subscriptionStatus === 'active' || subscriptionStatus === 'trialing';
    };

    // Wrapper function to persist brand selection
    const handleSetSelectedBrand = (brand) => {
        setSelectedBrand(brand);
        if (brand?.id) {
            setLastSelectedBrandId(brand.id);
        }
    };

    const value = {
        theme,
        toggleTheme,
        user: currentUser ? {
            name: currentUser.displayName || 'User',
            email: currentUser.email,
            avatar: currentUser.photoURL,
            uid: currentUser.uid
        } : null,
        brands,
        selectedBrand,
        setSelectedBrand: handleSetSelectedBrand,
        addBrand,
        updateBrand,
        deleteBrand,
        creatives,
        addCreative,
        updateCreative,
        deleteCreative,
        scheduledPosts,
        schedulePost,
        unschedulePost,
        reschedulePost,
        team,
        addTeamMember,
        updateTeamMember,
        activityLog,
        logActivity,
        uploadDocument,
        analytics,
        insights,
        automationSettings,
        setAutomationSettings,
        aiDecisions,
        // Billing
        creditBalance,
        subscriptionStatus,
        subscriptionTier,
        billingLoading,
        canAfford,
        formatCredits,
        creditsToUSD,
        hasActiveSubscription,
    };

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
